/**
 * © 2026 Desktronic. All rights reserved.
 * Proprietary code. Unauthorized copying, distribution or use
 * without written permission from Desktronic is prohibited.
 * Source: desktronic.lt | desktronicline.com | desktronic.se | desktronic.pl | desktronic.com/pl | desktronic.fr | desktronic.co.uk | desktronic.be | desktronic.nl | desktronic.dk | desktronic.com |  desktronic.cz
 */

// sig:dt-a7f3c91e04b2
class Products extends HTMLElement {
    constructor() {
      super();
      this.container = this.querySelector(".product-grid");
      this.variants = window.variants || [];
      this.allProductsQnt = this.variants.length;
      this.selectedFilters = {};
      this.sortType = "";
      this.initSortDropdown();
      this.qnt = document.querySelector(".desk_all-products-header-count .span");
      this.mobileQnt = this.querySelector(
        "#all-filters .mobile-footer .mobile-footer__qnt"
      );
      this.card = false;
      this.itemsPerPage = 40;
      this.inputs = this.querySelectorAll("#all-filters .filter-block input");
      this.isFirstLoad = true;

      // Preselected (locked) filters passed from the section via metafields
      this.preselectedFilters = null;
      const preselectedFiltersData = this.dataset.preselectedFilters;
      if (preselectedFiltersData && preselectedFiltersData.trim()) {
        try {
          const normalized = preselectedFiltersData.replace(
            /([{,]\s*)([A-Za-z0-9_]+)\s*:/g,
            '$1"$2":'
          );
          const preselected = JSON.parse(normalized);
          this.preselectedFilters = {};
          Object.entries(preselected).forEach(([key, value]) => {
            const arr = Array.isArray(value) ? value : [value];
            this.preselectedFilters[key] = arr;
            this.selectedFilters[key] = arr.slice();
            arr.forEach((val) => {
              const input = this.querySelector(
                `#all-filters input[data-category="${key}"][value="${val}"]`
              );
              if (input) input.checked = true;
            });
          });
        } catch (err) {
          console.warn("Failed to parse preselected filters", err);
          this.preselectedFilters = null;
        }
      }
  
      // Sync the grid layout to the selected view. localStorage wins if set;
      // otherwise fall back to whichever radio is checked in the markup
      // (multi view is the default).
      const savedView = localStorage.getItem("gridView_v2");
      const multiViewInput = this.querySelector(
        '.view-switcher input[value="multi"]'
      );
      const oneViewInput = this.querySelector(
        '.view-switcher input[value="one"]'
      );
      let useMulti;
      if (savedView === "multi") useMulti = true;
      else if (savedView === "single") useMulti = false;
      else useMulti = !!multiViewInput?.checked;

      if (useMulti) {
        if (multiViewInput) multiViewInput.checked = true;
        this.container.classList.add("multi");
      } else {
        if (oneViewInput) oneViewInput.checked = true;
        this.container.classList.remove("multi");
      }
  
      this.querySelectorAll(".view-switcher input").forEach((input) => {
        input.addEventListener("change", this.viewSwitch.bind(this));
      });
  
      this.querySelector("#all-filters .mobile-footer").addEventListener(
        "click",
        this.mobileFilterClose.bind(this)
      );
      this.querySelector(".mobile-head__wrapper svg").addEventListener(
        "click",
        this.mobileFilterClose.bind(this)
      );
      this.querySelector("#mobile-filter-toggle").addEventListener(
        "click",
        this.mobileFilterOpen.bind(this)
      );
      document.getElementById("filter-toggle").addEventListener(
        "click",
        this.toggleFilterBlock.bind(this)
      );
      this.querySelector(
        ".used-filters .used-filters__clear.mobile"
      ).addEventListener("click", this.clearAllFilters.bind(this));
      this.querySelector(
        ".used-filters .used-filters__clear.desktop"
      ).addEventListener("click", this.clearAllFilters.bind(this));
    }
  
    isProductAvailable(item) {
      if (!item) return false;
  
      // ✅ If it's a grouped card with multiple inner size variants
      if (Array.isArray(item.variants) && item.variants.length > 0) {
        return item.variants.some(
          (v) =>
            v.available === true || v.available === "true" || v.available === 1
        );
      }
  
      // ✅ If it's a simple product (no inner variants)
      return (
        item.available === true ||
        item.available === "true" ||
        item.available === 1
      );
    }
  
    getFirstBadgedVariant(product) {
      // If grouped product (with variants)
      if (Array.isArray(product.variants)) {
        return (
          product.variants.find(
            (v) => Array.isArray(v.badges) && v.badges.length > 0
          ) || null
        );
      }
  
      // If flat product with badges on itself
      if (Array.isArray(product.badges) && product.badges.length > 0) {
        return product; // treat product itself as the "variant"
      }
  
      return null;
    }
  
    toggleFilterBlock() {
      document.getElementById("filter-toggle").classList.toggle("is-visible");
      this.querySelector("#all-filters").classList.toggle("is-visible");
      this.updateFiltersHeight();
    }

    // Write a height onto the sticky filter panel. On mobile the panel is a
    // popup overlay, so we always clear it there. Skips the DOM write (and its
    // reflow) when the value hasn't changed.
    applyFiltersHeight(value) {
      const filters = this.querySelector(
        ".all-products-body__filters.is-visible"
      );
      if (!filters) {
        this._lastFiltersHeight = null;
        return;
      }
      if (window.innerWidth <= 992) value = "";

      if (value === this._lastFiltersHeight) return;
      this._lastFiltersHeight = value;
      filters.style.maxHeight = value;
      filters.style.height = value;
    }

    // Compute the exact height: fill from the panel's top down to the viewport
    // bottom, but never past the bottom of the product grid — so once the
    // grid's bottom is in view the panel ends exactly with it.
    updateFiltersHeight() {
      const filters = this.querySelector(
        ".all-products-body__filters.is-visible"
      );
      if (!filters) {
        this._lastFiltersHeight = null;
        return;
      }
      if (window.innerWidth <= 992) {
        this.applyFiltersHeight("");
        return;
      }

      const wrapper = this.querySelector(".product-grid-wrapper");
      const wrapperBottom = wrapper
        ? wrapper.getBoundingClientRect().bottom
        : Infinity;
      const top = filters.getBoundingClientRect().top;
      const available = Math.max(
        0,
        Math.round(Math.min(window.innerHeight, wrapperBottom) - top)
      );
      this.applyFiltersHeight(`${available}px`);
    }

    connectedCallback() {
      this.initFiltersFromURL();

      // Sticky filter panel height. While the user is scrolling we just pin it
      // to 100vh (a single cheap write, no layout reads — so zero scroll lag);
      // when scrolling stops we compute the exact height. The site uses Lenis
      // smooth scroll (native window "scroll" events are unreliable), so we
      // hook Lenis's own scroll event.
      this.updateFiltersHeight = this.updateFiltersHeight.bind(this);
      this.applyFiltersHeight = this.applyFiltersHeight.bind(this);
      this.updateFiltersHeight();
      window.addEventListener("load", this.updateFiltersHeight);
      window.addEventListener("resize", this.updateFiltersHeight);

      // Re-apply pagination when crossing the mobile/desktop breakpoint so the
      // numbered pagination (desktop) and "show more" button (mobile) swap in.
      this._wasMobile = window.innerWidth <= 992;
      window.addEventListener("resize", () => {
        const isMobile = window.innerWidth <= 992;
        if (isMobile !== this._wasMobile) {
          this._wasMobile = isMobile;
          this._mobileVisible = null;
          if (this._applyPagination) this._applyPagination();
        }
      });

      let filtersHeightTimer = null;
      let isScrolling = false;
      const onScroll = () => {
        if (!isScrolling) {
          isScrolling = true;
          this.applyFiltersHeight("100vh"); // scroll start → full height
        }
        clearTimeout(filtersHeightTimer);
        filtersHeightTimer = setTimeout(() => {
          isScrolling = false;
          this.updateFiltersHeight(); // scroll stop → exact height
        }, 100);
      };
      const bindScroll = () => {
        if (window.lenis && typeof window.lenis.on === "function") {
          window.lenis.on("scroll", onScroll);
        } else {
          window.addEventListener("scroll", onScroll, { passive: true });
        }
      };
      // Lenis is created on $(document).ready, which may run after this.
      if (window.lenis) {
        bindScroll();
      } else {
        window.addEventListener("load", bindScroll);
      }

      document.addEventListener("variants:ready", (e) => {
        console.log(
          "🔹 Variants ready event received:",
          e.detail?.length,
          "variants"
        );
        this.variants = e.detail || [];
        this.allProductsQnt = this.variants.length;
        this.renderVariants();
        setTimeout(() => {
          const skeleton = document.querySelector(".psa_skeleton-out");
          skeleton?.classList.add("loaded");
          skeleton?.classList.add("hided");
          document.querySelector(".product-grid-wrapper").style.minHeight = "unset";
          // The grid renders asynchronously and reflows the page height after
          // ScrollReveal has already initialised the sections below (FAQ etc.).
          // On short collections the page can settle with those sections already
          // in view, so no scroll event ever fires and their reveal never
          // triggers. Nudge scroll/resize listeners to re-evaluate the layout.
          if (window.lenis && typeof window.lenis.resize === "function") {
            window.lenis.resize();
          }
          window.dispatchEvent(new Event("resize"));
          window.dispatchEvent(new Event("scroll"));
        }, 500);
        this.querySelector("#all-filters .filter-name__qnt.all").innerHTML =
          `(${this.allProductsQnt})`;
      });
  
      document.addEventListener("DOMContentLoaded", () => {
        const firstCard = document.querySelector(".filter-block__content #card");
        firstCard ? this.updateCard(firstCard) : "";
        this.updateUsedFilters();
        this.renderVariants();
        this.querySelectorAll("#all-filters input").forEach((input) => {
          if (input.dataset.type === "sort") {
            input.onclick = (event) => {
              const type = event.target.dataset.sort;
              const text = event.target.value;
              this.setSortType(type, text);
              this.updateMobileUsed(event.target);
            };
  
            return;
          }
  
          input.onclick = (event) => {
            const isRadio =
              event.target.type === "radio" && event.target.name !== "size";
            const matched = event.target.dataset.matched;
            const matchedClosed = event.target.dataset.closed
              ? event.target.dataset.closed
              : "";
            const card = event.target.parentNode.querySelector("#card");
  
            this.toggleFilter(
              event.target.dataset.category,
              event.target.value,
              isRadio,
              matched,
              matchedClosed !== "" ? matchedClosed : "",
              card ? card : ""
            );
          };
        });
  
        const firstCheckedInput = this.querySelector(
          '.filter-block__wrapper input[name="category"]:checked'
        );
        this.updateMatched(
          firstCheckedInput?.dataset.matched,
          firstCheckedInput?.dataset.closed
        );
  
        this.querySelector("#all-filters .filter-name__qnt.all").innerHTML =
          `(${this.allProductsQnt})`;
      });
    }
  
    initFiltersFromURL() {
      const params = new URLSearchParams(window.location.search);
  
      // Collect valid filters from UI
      const allowedFilters = {};
      this.querySelectorAll("#all-filters input[data-category]").forEach(
        (input) => {
          const category = input.dataset.category;
          const value = input.value;
          if (!category || !value) return;
  
          if (!allowedFilters[category]) {
            allowedFilters[category] = [];
          }
          if (!allowedFilters[category].includes(value)) {
            allowedFilters[category].push(value);
          }
        }
      );
  
      // Collect valid sort values
      const allowedSorts = Array.from(
        document.querySelectorAll(".ap_val[data]")
      ).map((el) => el.getAttribute("data"));
  
      this.selectedFilters = {};
  
      for (const key of params.keys()) {
        // SORT
        if (key === "sort") {
          const sortValue = params.get("sort");
          if (!allowedSorts.includes(sortValue)) continue;
  
          this.sortType = sortValue;

          const sortLabel = document.querySelector(
            `.ap_val[data="${sortValue}"]`
          );
          if (sortLabel) {
            document
              .querySelector(".ap_content .ap_val.active")
              ?.classList.remove("active");
            sortLabel.classList.add("active");
            document.querySelector(".ap_sort span").textContent =
              sortLabel.textContent.trim();
          }
          continue;
        }
  
        // FILTERS
        if (!allowedFilters[key]) continue;
  
        const validValues = params
          .getAll(key)
          .filter((value) => allowedFilters[key].includes(value));
  
        if (validValues.length) {
          this.selectedFilters[key] = validValues;
        }
      }
  
      // Re-apply preselected (locked) filters unless the URL overrides them
      if (this.preselectedFilters) {
        for (const pk in this.preselectedFilters) {
          if (!this.selectedFilters[pk]) {
            this.selectedFilters[pk] = this.preselectedFilters[pk].slice();
          }
        }
      }

      // Default sort fallback: prefer "most-popular" when nothing is in the URL
      if (!this.sortType) {
        const popularOption = document.querySelector(
          '.ap_content .ap_val[data="most-popular"]'
        );
        if (popularOption) {
          this.sortType = "most-popular";
          document
            .querySelector(".ap_content .ap_val.active")
            ?.classList.remove("active");
          popularOption.classList.add("active");
          const sortLabel = document.querySelector(".ap_sort span");
          if (sortLabel) sortLabel.textContent = popularOption.textContent.trim();
        } else {
          this.sortType =
            document.querySelector(".ap_sortiration")?.dataset.first || "";
        }
      }
  
      this.updateCheckboxes();
    }
  
    updateUrlParams() {
      const params = new URLSearchParams();
      for (const [key, values] of Object.entries(this.selectedFilters)) {
        values.forEach((value) => {
          params.append(key, value);
        });
      }
      params.append("sort", this.sortType);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      history.replaceState(null, "", newUrl);
    }
  
    toggleFilter(
      category,
      value,
      isRadio = false,
      matched = "",
      matchedClosed = "",
      card = false
    ) {
      if (isRadio) {
        this.clearAllFiltersWithoutRender();
        if (value === "All") {
          delete this.selectedFilters[category];
        } else {
          this.selectedFilters[category] = [value];
        }
  
        if (card) {
          this.updateCard(card);
        } else {
          this.card = false;
        }
        this.updateMatched(matched, matchedClosed);
      } else if (category == "size") {
        if (value === "All") {
          delete this.selectedFilters[category];
        } else {
          this.selectedFilters[category] = [value];
        }
      } else {
        if (!this.selectedFilters[category]) {
          this.selectedFilters[category] = [];
        }
  
        const index = this.selectedFilters[category].indexOf(value);
        if (index > -1) {
          this.selectedFilters[category].splice(index, 1);
        } else {
          this.selectedFilters[category].push(value);
        }
  
        if (this.selectedFilters[category].length === 0) {
          delete this.selectedFilters[category];
        }
  
        if (category == "category") {
          this.updateMatched();
        }
      }
  
      this.updateUsedFilters();
      this.renderVariants(category);
      this.updateUrlParams();
    }
  
    toggleSize(category) {
      delete this.selectedFilters[category];
      this.updateUsedFilters();
      this.renderVariants(category);
      this.updateUrlParams();
    }
  
    updateCard(card) {
      this.card = {
        image: card.dataset.image,
        title: card.dataset.title,
        subtitle: card.dataset.subtitle,
        label: card.dataset.label,
        link: card.dataset.link,
      };
    }
  
    updateMatched(matched = "", matchedClosed = "") {
      const allMatched = this.querySelector(
        '.filter-block__wrapper input[name="category"][value="All"]'
      );
      let matchedFilters;
      let closedMatchedFilters;

      const matchedSource = matched === "" ? allMatched?.dataset.matched : matched;
      const closedSource =
        matchedClosed !== "" ? matchedClosed : allMatched?.dataset.closed;

      // When no matched-filter data is defined (e.g. collections without the
      // matched_filter metafield), show every filter instead of hiding all.
      matchedFilters = matchedSource ? matchedSource.split(",") : null;
      closedMatchedFilters = closedSource ? closedSource.split(",") : [];

      this.querySelectorAll(
        "#all-filters .filter-block:not(.mobile-sort)"
      ).forEach((filter) => {
        const category = filter.dataset.category;
        const filterDetails = filter.querySelector(".filter-block__details");
        const isClosed = closedMatchedFilters.includes(category);

        if (matchedFilters === null || matchedFilters.includes(category)) {
          filter.classList.remove("hide");
          isClosed
            ? filterDetails.removeAttribute("open")
            : filterDetails.setAttribute("open", "");
        } else {
          filter.classList.add("hide");
        }
      });
    }
  
    updateUsedFilters() {
      const usedFiltersList = document.querySelector(".used-filters__list");
      usedFiltersList
        .querySelectorAll(".used-filters__item")
        .forEach((filter) => {
          filter.remove();
        });
  
      for (const category in this.selectedFilters) {
        if (this.preselectedFilters && this.preselectedFilters[category]) continue;
        this.selectedFilters[category].forEach((value) => {
          const filterElement = document.createElement("div");
          filterElement.classList.add("used-filters__item");
          filterElement.textContent = value;
  
          filterElement.addEventListener("click", () => {
            if (category === "size") {
              this.toggleSize(category);
            } else {
              this.toggleFilter(category, value, false);
            }
            this.updateCheckboxes();
          });
  
          usedFiltersList.appendChild(filterElement);
        });
      }
      this.updateCheckboxes();
    }
  
    updateCheckboxes() {
      document.querySelectorAll(".filter-checkbox").forEach((checkbox) => {
        const category = checkbox.dataset.category;
        const value = checkbox.value;

        if (
          (this.selectedFilters[category] &&
            this.selectedFilters[category].includes(value)) ||
          value == "All"
        ) {
          checkbox.checked = true;
        } else {
          checkbox.checked = false;
        }
      });

      this.syncTabsToCategory();
    }

    syncTabsToCategory() {
      const activeCategory = document.querySelector(
        '.filter-block__wrapper input[name="category"]:checked'
      );
      const value = activeCategory ? activeCategory.value : "All";
      let activeTab = null;
      document.querySelectorAll(".shopall_tab").forEach((tab) => {
        const match = tab.dataset.tab === value;
        tab.classList.toggle("is-active", match);
        tab.setAttribute("aria-selected", match ? "true" : "false");
        if (match) activeTab = tab;
      });
      this.scrollActiveTabIntoView(activeTab);
    }

    scrollActiveTabIntoView(activeTab) {
      const container = document.querySelector(".shopall_controls");
      if (!container || !activeTab) return;
      // only when the strip is actually scrollable (mobile overflow)
      if (container.scrollWidth <= container.clientWidth) return;

      const cRect = container.getBoundingClientRect();
      const aRect = activeTab.getBoundingClientRect();
      const offset =
        aRect.left -
        cRect.left -
        (container.clientWidth - activeTab.clientWidth) / 2;
      container.scrollTo({
        left: container.scrollLeft + offset,
        behavior: "smooth",
      });
    }
  
    _preselectedCopy() {
      return this.preselectedFilters
        ? JSON.parse(JSON.stringify(this.preselectedFilters))
        : {};
    }

    clearAllFiltersWithoutRender() {
      this.selectedFilters = this._preselectedCopy();
      this.updateUsedFilters();
    }
  
    clearAllFilters() {
      this.selectedFilters = this._preselectedCopy();
      this.updateUsedFilters();
      this.updateUrlParams();
      this.renderVariants();
      this.updateMatched();
    }
  
    setSortType(type, text) {
      this.sortType = type;
      document.querySelector(".ap_sort span").textContent = text;
      this.updateUrlParams();
      this.renderVariants();
    }
  
    filterVariants() {
      return this.variants.filter((variant) => {
        return Object.keys(this.selectedFilters).every((category) => {
          if (category === "options" || category === "size") {
            return variant[category].some((color) => {
              return this.selectedFilters[category].includes(color);
            });
          } else {
            return this.selectedFilters[category].includes(variant[category]);
          }
        });
      });
    }
  
    filterVariantsQnt(qntFilters, filtered = false) {
      if (filtered) {
        return filtered.filter((variant) => {
          return Object.keys(qntFilters).every((category) => {
            if (category === "options" || category === "size") {
              return variant[category].some((color) => {
                return qntFilters[category].includes(color);
              });
            } else {
              return qntFilters[category].includes(variant[category]);
            }
          });
        });
      } else {
        return this.variants.filter((variant) => {
          return Object.keys(qntFilters).every((category) => {
            if (category === "options" || category === "size") {
              return variant[category].some((color) => {
                return qntFilters[category].includes(color);
              });
            } else {
              return qntFilters[category].includes(variant[category]);
            }
          });
        });
      }
    }
  
    sortVariants(filteredVariants) {
      return filteredVariants.sort((a, b) => {
        // ✅ always push sold-out products to bottom regardless of sorting type
        const aAvailable = this.isProductAvailable(a);
        const bAvailable = this.isProductAvailable(b);
        if (aAvailable && !bAvailable) return -1;
        if (!aAvailable && bAvailable) return 1;
  
        // all other existing sort rules remain unchanged
        if (this.sortType === "price-high-low") {
          if (this.selectedFilters["size"]) {
            const getProductPrice = (product) => {
              if (product.variants) {
                const variant = product.variants.find(
                  (v) => v.variant_title === this.selectedFilters["size"][0]
                );
                if (variant) {
                  return parseFloat(variant.variant_price.replace(/[^\d.]/g, "")) * 100;
                }
              }
              return product.price_sort;
            };
            const aPrice = getProductPrice(a);
            const bPrice = getProductPrice(b);
            return bPrice - aPrice;
          } else {
            const getMaxPrice = (product) => {
              if (Array.isArray(product.variants) && product.variants.length > 0) {
                return Math.max(...product.variants.map((v) => parseFloat(v.variant_price.replace(/[^\d.]/g, "")) * 100));
              }
              return product.price_sort;
            };
            return getMaxPrice(b) - getMaxPrice(a);
          }
        } else if (this.sortType === "price-low-high") {
          if (this.selectedFilters["size"]) {
            const getProductPrice = (product) => {
              if (product.variants) {
                const variant = product.variants.find(
                  (v) => v.variant_title === this.selectedFilters["size"][0]
                );
                if (variant) {
                  return parseFloat(variant.variant_price.replace(/[^\d.]/g, "")) * 100;
                }
              }
              return product.price_sort;
            };
            const aPrice = getProductPrice(a);
            const bPrice = getProductPrice(b);
            return aPrice - bPrice;
          } else {
            const getMinPrice = (product) => {
              if (Array.isArray(product.variants) && product.variants.length > 0) {
                return Math.min(...product.variants.map((v) => parseFloat(v.variant_price.replace(/[^\d.]/g, "")) * 100));
              }
              return product.price_sort;
            };
            return getMinPrice(a) - getMinPrice(b);
          }
        } else if (this.sortType === "newest") {
          const getNewestPriority = (product) => {
            if (product.variants && product.variants.length > 0) {
              return Math.max(...product.variants.map((v) => v.variant_newest));
            }
            return -Infinity;
          };
          const aPriority = getNewestPriority(a);
          const bPriority = getNewestPriority(b);
          return bPriority - aPriority;
        } else if (this.sortType === "sale") {
          const getMaxSale = (product) => {
            if (Array.isArray(product.variants)) {
              return Math.max(
                ...product.variants.map((v) => parseFloat(v.sale) || 0)
              );
            }
            return parseFloat(product.sale) || 0;
          };
          return getMaxSale(b) - getMaxSale(a);
        } else if (this.sortType === "sale_euro") {
          const getMaxSaleEuro = (product) => {
            if (Array.isArray(product.variants)) {
              return Math.max(
                ...product.variants.map((v) => parseFloat(v.sale_euro) || 0)
              );
            }
            return parseFloat(product.sale_euro) || 0;
          };
          return getMaxSaleEuro(b) - getMaxSaleEuro(a);
        } else {
          // default sorting (e.g., bestseller)
          const aBadge = this.getFirstBadgedVariant(a);
          const bBadge = this.getFirstBadgedVariant(b);
          if (aBadge && !bBadge) return -1;
          if (!aBadge && bBadge) return 1;
          return 0;
        }
      });
    }
  
    initSortDropdown() {
      const dropdown = document.querySelector(".ap_sortiration");
      if (!dropdown) return;
      const dropdownToggle = dropdown.querySelector(".ap_sort");
      const nav = dropdown.closest(".ap_sorting-nav") || dropdown;

      // Clicking anywhere on the whole sorting nav toggles the dropdown,
      // same as clicking .ap_sort directly. Clicks on the options list
      // (.ap_content) are handled below and must not re-toggle.
      nav.onclick = (event) => {
        if (event.target.closest(".ap_content")) return;
        dropdownToggle.classList.toggle("active");
      };

      dropdown.querySelectorAll(".ap_val").forEach((option) => {
        option.onclick = (event) => {
          const type = event.target.getAttribute("data");
          const text = event.target.textContent.trim();
          dropdown
            .querySelector(".ap_val.active")
            ?.classList.remove("active");
          event.target.classList.add("active");
          this.setSortType(type, text);
          dropdownToggle.classList.remove("active");
        };
      });

      document.addEventListener("click", (event) => {
        if (!nav.contains(event.target)) {
          dropdownToggle.classList.remove("active");
        }
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          dropdownToggle.classList.remove("active");
        }
      });
    }
  
    updateQntFilters(filtered) {
      this.inputs.forEach((input) => {
        this.updateMobileUsed(input);
        if (input.type === "radio" && input.name !== "size") return;
        if (input.type === "hidden") return;
  
        let category = input.dataset.category;
        const value = input.value;
        if (input.checked) {
          const newFilters = {};
          if (category !== "size") {
            newFilters[category] = [];
            newFilters[category].push(value);
          }
          const count = this.filterVariantsQnt(newFilters, filtered).length;
          this.updateInput(input, count);
        } else {
          let qntFilters = JSON.parse(JSON.stringify(this.selectedFilters));
          if (!qntFilters[category] || category === "size") {
            qntFilters[category] = [];
          }
  
          const index = qntFilters[category].indexOf(value);
          index <= -1 || category !== "size"
            ? qntFilters[category].push(value)
            : "";
  
          const counter = this.filterVariantsQnt(qntFilters).length;
  
          if (filtered.length <= counter && category !== "size") {
            const count = counter - filtered.length;
            this.updateInput(input, count);
          } else {
            const count = counter;
            this.updateInput(input, count);
          }
  
          if (value === "All") {
            delete qntFilters.size;
            const counter = this.filterVariantsQnt(qntFilters).length;
            this.updateInput(input, counter);
          }
        }
      });
    }
  
    updateInput(input, count) {
      const parentElement = input.closest(".filter-item");
      if (!parentElement) return;
  
      const qntElement = parentElement.querySelector(".filter-name__qnt");
      if (qntElement) {
        qntElement.textContent = `(${count})`;
      }
  
      // ✅ Hide color bubbles if count is 0 and category is not "All" or "Desks"
      const currentCategory =
        this.selectedFilters["category"] &&
        this.selectedFilters["category"][0]?.toLowerCase();
  
      if (
        input.dataset.category === "options" &&
        currentCategory &&
        currentCategory !== "all" &&
        !currentCategory.includes("desk")
      ) {
        if (count === 0) {
          parentElement.style.display = "none";
        } else {
          parentElement.style.display = "";
        }
      } else {
        // Always show when “All” or “Desks” are selected
        parentElement.style.display = "";
      }
    }
  
    updateMobileUsed(input) {
      if (input.value == "All") return;
      const filterBlock = input.closest(".filter-block");
      if (!filterBlock) return;
  
      const usedBlock = filterBlock.querySelector(".filter-block__used");
      if (!usedBlock) return;
  
      let values = usedBlock.textContent.split(", ").filter(Boolean);
  
      if (input.checked) {
        if (!values.includes(input.value)) {
          values.push(input.value);
        }
      } else {
        values = values.filter((val) => val !== input.value);
      }
  
      usedBlock.textContent = values.join(", ");
    }
  
    optionClick(event) {
      event.preventDefault();
      const link = event.currentTarget.getAttribute("href");
      if (link) {
        window.location.href = link;
      }
    }
  
    optionHover(event) {
      const imageAlt = event.currentTarget.getAttribute("data-alt");
  
      if (imageAlt) {
        const parentImageLink = event.currentTarget.closest(
          ".product-item__image"
        );
        const images = parentImageLink?.querySelectorAll("img");
        images.forEach((image) => {
          if (image.alt === imageAlt) {
            if (!image.getAttribute("src") && image.dataset.src) {
              image.src = image.dataset.src;
            }
            image.classList.add("active-hover");
          } else {
            image.classList.remove("active-hover");
          }
        });
      }
  
      const priceBlock = event.currentTarget.getAttribute("data-price");
      const priceBlockCompare = event.currentTarget.getAttribute("data-compare");
       const variantId = event.currentTarget.getAttribute("data-variant-id").replace(/\D/g, "");
  
      if (priceBlock) {
        const priceBlockVisible = event.currentTarget
          .closest(".product-item")
          .querySelector(".price-block--hidden");
        priceBlockVisible.querySelector(".price-block__price").textContent =
          priceBlock;
        priceBlockVisible.querySelector(".price-block__price").dataset.variantId = variantId;
        priceBlockVisible.querySelector(".price-block__compare").textContent =
          priceBlockCompare;
        priceBlockVisible.querySelector(".price-block__compare").dataset.variantId = variantId;
        
        event.currentTarget
          .closest(".product-item")
          .querySelector(".price-block--visible")
          .classList.add("hidden");
        event.currentTarget
          .closest(".product-item")
          .querySelector(".price-block--hidden")
          .classList.remove("hidden");
      }

      // Keep the sale badge in sync with the hovered variant's price.
      this.syncHoverSaleBadge(event.currentTarget, priceBlock, priceBlockCompare);
    }

    syncHoverSaleBadge(optionEl, priceStr, compareStr) {
      var productItem = optionEl.closest(".product-item");
      var badge = productItem && productItem.querySelector(".badge-sale");
      if (!badge || !window.deskDiscountBadgeValue) return;
      if (badge.dataset.origHtml == null) badge.dataset.origHtml = badge.innerHTML;
      var p = window.deskPriceToNumber(priceStr);
      var c = window.deskPriceToNumber(compareStr);
      var pct = c > p ? Math.round(((c - p) / c) * 100) : 0;
      var val = pct > 0 ? window.deskDiscountBadgeValue(pct, priceStr, compareStr) : "";
      if (val) {
        var saleBadge = this.dataset.saleBadge || "";
        var span = badge.querySelector("span");
        if (span) span.textContent = saleBadge.includes("|") ? saleBadge.replace("|", val) : saleBadge + " " + val;
        badge.style.display = "";
      } else {
        badge.style.display = "none";
      }
    }

    optionHoverLeave(event) {
      const parentImageLink = event.target;

      if (parentImageLink) {
        const images = parentImageLink.querySelectorAll("img");
        images.forEach((img) => img.classList.remove("active-hover"));
      }

      const productItem = event.currentTarget.closest(".product-item");
      if (!productItem) return;

      // Restore the sale badge to the default variant's value.
      var restoreBadge = productItem.querySelector(".badge-sale");
      if (restoreBadge && restoreBadge.dataset.origHtml != null) {
        restoreBadge.innerHTML = restoreBadge.dataset.origHtml;
        restoreBadge.style.display = "";
        delete restoreBadge.dataset.origHtml;
      }

      const priceBlockHidden = productItem.querySelector(".price-block--hidden");
      // only some cards have the hover price block — skip the rest gracefully
      if (!priceBlockHidden) return;

      priceBlockHidden.querySelector(".price-block__price").textContent = "";
      priceBlockHidden.querySelector(".price-block__compare").textContent = "";
      priceBlockHidden.classList.add("hidden");
      productItem
        .querySelector(".price-block--visible")
        ?.classList.remove("hidden");
    }
  
    renderVariants() {
      if (!this.container) return;
  
      let filtered = this.filterVariants();
      filtered = this.sortVariants(filtered);
      // console.log(`🔹 Sorted by: ${this.sortType}`, filtered);
  
      // console.table(
      //   filtered.map(v => ({
      //     product_title: v.product_title,
      //     color_group: v.color_group || '',
      //     sale_euro: v.variants && v.variants.length > 0
      //       ? v.variants[0].sale_euro
      //       : v.sale_euro
      //   }))
      // );
  
      console.table(
        filtered.map((product) => {
          const firstBadged = this.getFirstBadgedVariant(product);
          return {
            product_title: product.product_title,
            color_group: product.color_group || "",
            has_bestseller: !!firstBadged,
          };
        })
      );
  
      filtered.forEach((p) => {
        if (
          p.product_title.includes("Kabelkanal") ||
          p.product_title.includes("Cable")
        ) {
          console.log(
            "🔍 Inspecting badges for",
            p.product_title,
            this.getFirstBadgedVariant(p)
          );
        }
      });
  
      this.updateQntFilters(filtered);
      this.qnt.innerHTML = filtered.length;
      let extraCount = 0;
      let nextInsertIndex = 8;
  
      this.container.innerHTML = filtered
        .map((variant, index) => {
          let extraItem = "";
  
          if (this.card && index + 1 === nextInsertIndex) {
            extraItem = `
            <li class="product-item card">
              <a class="card__wrapper" href="${this.card.link}">
                <div class="card__text">                
                  <h4 class="card__title">${this.card.title}</h4>
                  <p class="card__subtitle">${this.card.subtitle}</p>
                  <div class="card__button">
                    <span class="card__label">${this.card.label}</span>
                    ${
                      this.card.label !== ""
                        ? `<svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L5 5.5L1 10" stroke="#EFEEE9" />
                        </svg>`
                        : ``
                    }
                  </div>
                </div>
                <div class="card__overlay"></div>
                <img class="card__image" src="${window.location.origin}/cdn/shop/${this.card.image}" alt="${this.card.title}">
              </a>
            </li>
          `;
            extraCount++;
            nextInsertIndex += 8 + extraCount;
          }
  
          if (variant.color_group) {
            const prices = variant.variants.map((v) =>
              parseFloat(v.variant_price.replace(/[^\d,.]/g, ""))
            );
            const compares = variant.variants
              .map((v) =>
                parseFloat(v.variant_compare_price?.replace(/[^\d,.]/g, ""))
              )
              .filter(Boolean);
            const samplePrice = variant.variants[0].variant_price.trim();
            const currencyBefore = /^[^\d]/.test(samplePrice);
            const currencySymbol = samplePrice.replace(/[\d.,\s]/g, "").trim();
            const hasSpace =
              /\d\s[^\d]/.test(samplePrice) || /[^\d]\s\d/.test(samplePrice);
            const space = hasSpace ? " " : "";
  
            // Get the variant id of the cheapest variant and the most expensive one, from the variant_id variable
            const cheapestVariantId = variant.variants[prices.indexOf(Math.min(...prices))].variant_id;
            const mostExpensiveVariantId = variant.variants[prices.indexOf(Math.max(...prices))].variant_id;
            // Extract only numeric values from cheapestVariantId and mostExpensiveVariantId
            const cheapestVariantIdNumeric = cheapestVariantId.replace(/\D/g, "");
            const mostExpensiveVariantIdNumeric = mostExpensiveVariantId.replace(/\D/g, "");
  
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            const minCompare = compares.length ? Math.min(...compares) : null;
            const maxCompare = compares.length ? Math.max(...compares) : null;
  
            let priceRange;
            if (minPrice === maxPrice) {
              // single price
              priceRange = currencyBefore
                ? `${currencySymbol}${space}${minPrice}`
                : `${minPrice}${space}${currencySymbol}`;
  
              // add compare if available
              if (minCompare && minCompare > minPrice) {
                const comparePrice = currencyBefore
                  ? `${currencySymbol}${space}${minCompare}`
                  : `${minCompare}${space}${currencySymbol}`;
                priceRange = `<span class="price-block__price onSale">${priceRange}</span>
                              <span class="price-block__compare">${comparePrice}</span>`;
              } else {
                priceRange = `<span class="price-block__price">${priceRange}</span>`;
              }
            } else {
              // price range
              const rangeText = currencyBefore
                ? `<span class="igPrice" data-variant-id="${cheapestVariantIdNumeric}">${currencySymbol}${space}${minPrice}</span> - <span class="igPrice" data-variant-id="${mostExpensiveVariantIdNumeric}">${currencySymbol}${space}${maxPrice}</span>`
                : `<span class="igPrice" data-variant-id="${cheapestVariantIdNumeric}">${minPrice}${space}${currencySymbol}</span> - <span class="igPrice" data-variant-id="${mostExpensiveVariantIdNumeric}">${maxPrice}${space}${currencySymbol}</span>`;
              priceRange = `<span class="price-block__price">${rangeText}</span>`;
            }
  
            // const prices = variant.variants.map((v) => {
            //   return parseFloat(v.variant_price.replace(/[^\d.]/g, ""));
            // });
            // const currencySymbol = variant.variants[0].variant_price
            //   .replace(/[\d.]/g, "")
            //   .trim();
            // const priceRange = `${Math.min(...prices)} ${currencySymbol} - ${Math.max(...prices)} ${currencySymbol}`;
  
            return `
            <li class="product-item" data-index="${index}">
              <a 
                class="product-item__image color_group" 
                href="${
                  this.selectedFilters.size
                    ? variant.variants.find(
                        (v) => v.variant_title === this.selectedFilters.size[0]
                      )?.variant_link || variant.url
                    : variant.url
                }
                ">
                ${variant.variants
                  .map((option, index) => {
                    const isActive = this.selectedFilters.size
                      ? option.variant_title === this.selectedFilters.size[0]
                      : option.variant_title === variant.basic_color
                        ? true
                        : index === 0;
                    return `<img ${isActive ? `class="active"` : ""} loading="lazy" decoding="async" ${isActive ? "src" : "data-src"}="${window.location.origin}/cdn/shop/${option.image_url}" alt="${option.variant_title}">`;
                  })
                  .join("")}
                ${
                  !this.isProductAvailable(variant)
                    ? `<div class="badge-sold">${this.dataset.soldBadge}</div>`
                    : `<div class="badges-block">
                      ${
                        this.selectedFilters.size
                          ? `
                        ${(() => {
                          const selectedVariant = variant.variants.find(
                            (v) =>
                              v.variant_title === this.selectedFilters.size[0]
                          );
                          return selectedVariant
                            ? selectedVariant.badges
                                .map(
                                  (badge) => `
                            <div class="badge-basic">
                              <span>${badge.name}</span>
                            </div>
                          `
                                )
                                .join("")
                            : "";
                        })()}
                      `
                          : (() => {
                              const firstVariantWithBadges =
                                variant.variants.find(
                                  (v) => v.badges && v.badges.length > 0
                                );
                              return firstVariantWithBadges
                                ? firstVariantWithBadges.badges
                                    .map(
                                      (badge) => `
                          <div class="badge-basic">
                            <span>${badge.name}</span>
                          </div>
                        `
                                    )
                                    .join("")
                                : "";
                            })()
                      }
  
                      ${(() => {
                        let selectedVariant;
                        let saleBadge = this.dataset.saleBadge;
                        if (this.selectedFilters.size) {
                          selectedVariant = variant.variants.find(
                            (v) =>
                              v.variant_title === this.selectedFilters.size[0]
                          );
                        } else {
                          saleBadge = this.dataset.saleAllBadge;
                          selectedVariant = variant.variants.reduce(
                            (max, current) => {
                              const currentSale = current.sale || 0;
                              const maxSale = max.sale || 0;
                              return currentSale > maxSale ? current : max;
                            }
                          );
                        }
                        return selectedVariant && selectedVariant.sale > 0
                          ? `
                            <div class="badge-sale">
                              <span>${saleBadge.includes("|") ? saleBadge.replace("|", window.deskDiscountBadgeValue(selectedVariant.sale, selectedVariant.variant_price, selectedVariant.variant_compare_price)) : `${saleBadge} ${window.deskDiscountBadgeValue(selectedVariant.sale, selectedVariant.variant_price, selectedVariant.variant_compare_price)}`}</span>
                            </div>
                          `
                          : "";
                      })()}
                      </div>
                    `
                }
  
                ${
                  variant.variants.length > 1 && !this.selectedFilters.size
                    ? `
                    <ul class="product-item__options">
                      ${variant.variants
                        .map(
                          (option) => `
                        <div
                          id="color-group-option"
                          class="option"
                          data-variant-id="${option.variant_id.replace(/\D/g, "")}"
                          href="${option.variant_link}"
                          data-image="${option.image_url}"
                          data-alt="${option.variant_title}"
                          data-price="${option.variant_price}"
                          data-compare="${option.variant_compare_price}"
                        >
                          <span>${option.variant_title}</span>
                        </div>
                      `
                        )
                        .join("")}
                    </ul>
                  `
                    : ""
                }
                
              </a>
              <div class="product-item__info">
                ${
                  variant.rating != ""
                    ? `
                  <div class="ruk_rating_snippet" data-sku="${variant.id}" data-product-group="${variant.review_group || ""}">
                    <span>
                      <svg width="63" height="10" viewBox="0 0 63 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.25731 0L6.75844 3.46174L10.5146 3.81966L7.68619 6.31705L8.50651 10L5.25731 8.08174L2.00811 10L2.82843 6.31705L0 3.81966L3.75618 3.46174L5.25731 0Z" fill="#E2AE44"/>
                        <path d="M18.3042 0L19.8053 3.46174L23.5615 3.81966L20.7331 6.31705L21.5534 10L18.3042 8.08174L15.055 10L15.8753 6.31705L13.0469 3.81966L16.8031 3.46174L18.3042 0Z" fill="#E2AE44"/>
                        <path d="M31.3432 0L32.8444 3.46174L36.6006 3.81966L33.7721 6.31705L34.5924 10L31.3432 8.08174L28.0941 10L28.9144 6.31705L26.0859 3.81966L29.8421 3.46174L31.3432 0Z" fill="#E2AE44"/>
                        <path d="M44.3901 0L45.8913 3.46174L49.6474 3.81966L46.819 6.31705L47.6393 10L44.3901 8.08174L41.1409 10L41.9612 6.31705L39.1328 3.81966L42.889 3.46174L44.3901 0Z" fill="#E2AE44"/>
                        <path d="M57.4292 0L58.9303 3.46174L62.6865 3.81966L59.8581 6.31705L60.6784 10L57.4292 8.08174L54.18 10L55.0003 6.31705L52.1719 3.81966L55.9281 3.46174L57.4292 0Z" fill="#E2AE44"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M57.4297 8.08185V0.000976562L58.9303 3.46156L62.6865 3.81948L59.8581 6.31687L60.6784 9.99982L57.4297 8.08185Z" fill="#CCCCCC"/>
                      </svg> 
                    </span>
                    <span class="product-item__count">${Math.round(Number(variant.rating) * 10) / 10}</span>
                    <span class="product-item__reviews main_product_rating_actual__js_carted">${variant.review_count ? `(${variant.review_count})` : ""}</span>
                  </div>
                `
                    : ""
                }
                <a class="product-item__link" href="${
                  this.selectedFilters.size
                    ? variant.variants.find(
                        (v) => v.variant_title === this.selectedFilters.size[0]
                      )?.variant_link
                    : variant.variants.find(
                        (v) => v.variant_title === variant.basic_color
                      )?.variant_link || variant.url
                }">
                  <h3 class="product-item__title">${this.dataset.showCustomNamings === 'true' && variant.collection_name ? variant.collection_name : variant.product_title}</br>${variant.color_group.replaceAll("/", " + ")}</h3>
                </a>
                <p class="product-item__variants">${variant.variants.length} ${this.dataset.availableText}</p>
                <div class="price-block price-block--visible">
                  ${
                    this.selectedFilters.size
                      ? (() => {
                          const selectedSize = this.selectedFilters.size[0];
                          const matchingVariant = variant.variants.find(
                            (v) => v.variant_title === selectedSize
                          );
                          return matchingVariant
                            ? `<span class="price-block__price onSale">${matchingVariant.variant_price}</span>
                        ${matchingVariant.variant_compare_price ? `<span class="price-block__compare">${matchingVariant.variant_compare_price}</span>` : ""}`
                            : (() => {
                                const fallbackVariant =
                                  variant.variants.find(
                                    (v) => v.variant_title === variant.basic_color
                                  ) || variant.variants[0];
                                return `<span class="price-block__price">${priceRange}</span>`;
                              })();
                        })()
                      : (() => {
                          const fallbackVariant =
                            variant.variants.find(
                              (v) => v.variant_title === variant.basic_color
                            ) || variant.variants[0];
                          return `<span class="price-block__price">${priceRange}</span>`;
                        })()
                  }
                </div>
                <div class="price-block price-block--hidden hidden">
                  <span class="price-block__price onSale igPrice" data-variant-id="${variant.id.replace(/\D/g, "")}"></span>
                  <span class="price-block__compare" data-variant-id="${variant.id.replace(/\D/g, "")}"></span>
                </div>
              </div>
            </li>
            ${extraItem}
          `;
          }
  
          return `
          <li class="product-item" data-index="${index}">
            <a class="product-item__image" href="${variant.url}">
              <img class="active" loading="lazy" decoding="async" src="${window.location.origin}/cdn/shop/${variant.image_url}" alt="${variant.title}">
              ${
                !this.isProductAvailable(variant)
                  ? `<div class="badge-sold">${this.dataset.soldBadge}</div>`
                  : `<div class="badges-block">
                    ${variant.badges
                      .map(
                        (badge) => `
                      <div class="badge-basic">
                        <span>${badge.name}</span>
                      </div>
                    `
                      )
                      .join("")}
                    ${
                      variant.sale > 0
                        ? `
                      <div class="badge-sale">
                        <span>${this.dataset.saleBadge}</span>
                        <span>${window.deskDiscountBadgeValue(variant.sale, variant.price, variant.compare)}</span>
                      </div>
                    `
                        : ""
                    }
                  </div>`
              }
            </a>
            <div class="product-item__info">
              ${
                variant.rating != ""
                  ? `
                <div class="ruk_rating_snippet" data-sku="${variant.id}" data-product-group="${variant.review_group || ""}">
                    <span>
                      <svg width="63" height="10" viewBox="0 0 63 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.25731 0L6.75844 3.46174L10.5146 3.81966L7.68619 6.31705L8.50651 10L5.25731 8.08174L2.00811 10L2.82843 6.31705L0 3.81966L3.75618 3.46174L5.25731 0Z" fill="#E2AE44"/>
                        <path d="M18.3042 0L19.8053 3.46174L23.5615 3.81966L20.7331 6.31705L21.5534 10L18.3042 8.08174L15.055 10L15.8753 6.31705L13.0469 3.81966L16.8031 3.46174L18.3042 0Z" fill="#E2AE44"/>
                        <path d="M31.3432 0L32.8444 3.46174L36.6006 3.81966L33.7721 6.31705L34.5924 10L31.3432 8.08174L28.0941 10L28.9144 6.31705L26.0859 3.81966L29.8421 3.46174L31.3432 0Z" fill="#E2AE44"/>
                        <path d="M44.3901 0L45.8913 3.46174L49.6474 3.81966L46.819 6.31705L47.6393 10L44.3901 8.08174L41.1409 10L41.9612 6.31705L39.1328 3.81966L42.889 3.46174L44.3901 0Z" fill="#E2AE44"/>
                        <path d="M57.4292 0L58.9303 3.46174L62.6865 3.81966L59.8581 6.31705L60.6784 10L57.4292 8.08174L54.18 10L55.0003 6.31705L52.1719 3.81966L55.9281 3.46174L57.4292 0Z" fill="#E2AE44"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M57.4297 8.08185V0.000976562L58.9303 3.46156L62.6865 3.81948L59.8581 6.31687L60.6784 9.99982L57.4297 8.08185Z" fill="#CCCCCC"/>
                      </svg> 
                    </span>
                    <span class="product-item__count">${Math.round(Number(variant.rating) * 10) / 10}</span>
                    <span class="product-item__reviews main_product_rating_actual__js_carted">${variant.review_count ? `(${variant.review_count})` : ""}</span>
                  </div>
                `
                  : ""
              }
  
              <a class="product-item__link" href="${variant.url}">
                <h3 class="product-item__title">${this.dataset.showCustomNamings === 'true' && variant.collection_name ? variant.collection_name : variant.product_title}</h3>
              </a>
              <p class="product-item__variants">${variant.description}</p>
              <div class="price-block">
                <span class="price-block__price ${variant.compare && variant.compare !== "" ? "onSale" : ""} igPrice" data-variant-id="${variant.id.replace(/\D/g, "")}">${variant.price}</span>
                ${variant.compare && variant.compare !== "" ? `<span class="price-block__compare" data-variant-id="${variant.id.replace(/\D/g, "")}">${variant.compare}</span>` : ""}
              </div>
            </div>
          </li>
          ${extraItem}
        `;
        })
        .join("");
  
      // Pagination
      const itemsPerPage = 36;
      const totalPages = Math.ceil(filtered.length / itemsPerPage);
      let currentPage = 0;
  
      function generatePagination() {
        let paginationHTML = `
          <button class="pagination-prev" disabled>
            <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 14L1 7.5L7.5 1" stroke="#19191C" />
            </svg>
          </button>
        `;
        let pages = [];
  
        if (totalPages <= 5) {
          for (let i = 0; i < totalPages; i++) {
            pages.push(i);
          }
        } else {
          if (currentPage <= 3) {
            pages = [0, 1, 2, 3, "...", totalPages - 1];
          } else if (currentPage >= totalPages - 4) {
            pages = [
              0,
              "...",
              totalPages - 4,
              totalPages - 3,
              totalPages - 2,
              totalPages - 1,
            ];
          } else {
            pages = [
              0,
              "...",
              currentPage - 1,
              currentPage,
              currentPage + 1,
              "...",
              totalPages - 1,
            ];
          }
        }
  
        paginationHTML += pages
          .map((page) =>
            page === "..."
              ? `<span class="pagination-dots">...</span>`
              : `<button class="pagination-btn" data-page="${page}">${page + 1}</button>`
          )
          .join("");
  
        paginationHTML += `
          <button class="pagination-next">
            <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L7.5 7.5L1 14" stroke="#19191C" />
            </svg>
          </button>
        `;
  
        document.querySelector(".pagination")?.remove();
        document
          .querySelector(".product-grid")
          .insertAdjacentHTML(
            "afterend",
            `<div class="pagination Desktop_only">${paginationHTML}</div>`
          );
      }
  
      function updatePagination(page, isFirstLoad = false) {
        if (!isFirstLoad) scrollToTop();
        currentPage = page;
        document.querySelectorAll(".product-item").forEach((item, index) => {
          item.style.display =
            index >= page * itemsPerPage && index < (page + 1) * itemsPerPage
              ? "block"
              : "none";
        });
        generatePagination();
        document.querySelectorAll(".pagination-btn").forEach((btn) => {
          btn.classList.toggle("active", parseInt(btn.dataset.page) === page);
          btn.addEventListener("click", () =>
            updatePagination(parseInt(btn.dataset.page))
          );
        });
        document.querySelector(".pagination-prev").disabled = page === 0;
        document.querySelector(".pagination-next").disabled =
          page === totalPages - 1;
        document
          .querySelector(".pagination-prev")
          .addEventListener("click", () => updatePagination(currentPage - 1));
        document
          .querySelector(".pagination-next")
          .addEventListener("click", () => updatePagination(currentPage + 1));
      }
  
      // Mobile uses a "Show more" button instead of numbered pagination:
      // 12 products in single-column view, 24 in multi-column view, loading
      // more on each click. Desktop keeps the numbered pagination (which is
      // hidden on mobile via the .Desktop_only class).
      const mobilePageSize = () =>
        this.container.classList.contains("multi") ? 24 : 12;

      this._applyPagination = () => {
        const isMobile = window.innerWidth <= 992;
        const items = this.querySelectorAll(".product-item");
        const wrapper = this.querySelector(".product-grid-wrapper");
        let block = wrapper.querySelector(".load-more-block");

        if (!isMobile) {
          block?.remove();
          updatePagination(currentPage, this.isFirstLoad);
          return;
        }

        // Mobile: drop the numbered pagination and reveal items up to the limit.
        document.querySelector(".pagination")?.remove();
        if (this._mobileVisible == null) this._mobileVisible = mobilePageSize();
        const visible = Math.min(this._mobileVisible, items.length);
        items.forEach((item, index) => {
          item.style.display = index < visible ? "block" : "none";
        });

        if (!block) {
          block = document.createElement("div");
          block.className = "load-more-block";

          const fade = document.createElement("div");
          fade.className = "load-more-fade";

          const loadMoreBtn = document.createElement("button");
          loadMoreBtn.className = "load-more";
          loadMoreBtn.textContent = this.dataset.loadMore || "Show more";
          loadMoreBtn.addEventListener("click", () => {
            this._mobileVisible += mobilePageSize();
            this._applyPagination();
          });

          block.appendChild(fade);
          block.appendChild(loadMoreBtn);
          wrapper.appendChild(block);
        }

        // Show the fade + button only while there are more products to load.
        block.style.display = visible >= items.length ? "none" : "";
      };

      this._mobileVisible = null; // reset to the initial limit on every render
      this._applyPagination();

      // update mobile qnt selected filters
      const usedFiltersList = document.querySelector(".used-filters__list");
      const filteredQnt = document.querySelector("#filtered-mobile-qnt");
      if (usedFiltersList && filteredQnt) {
        const count = usedFiltersList.children.length - 1;
        filteredQnt.textContent = count > 0 ? `${count}` : "";
      }
  
      // update mobile qnt selected products
      this.allProductsQnt != filtered.length
        ? (this.mobileQnt.innerHTML = `(${filtered.length})`)
        : (this.mobileQnt.innerHTML = "");
  
      // scroll to top
      if (!this.isFirstLoad) scrollToTop();
      this.isFirstLoad = false;
  
      function scrollToTop() {
        const section = document.querySelector(".all-products");
  
        if (section) {
          const top = section.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
  
      this.querySelectorAll("#color-group-option").forEach((option) => {
        option.addEventListener("click", (event) => this.optionClick(event));
        option.addEventListener("mouseenter", (event) => this.optionHover(event));
      });
  
      this.querySelectorAll(".product-item__image").forEach((productItem) => {
        productItem.addEventListener("mouseleave", (event) =>
          this.optionHoverLeave(event)
        );
      });

      this.appendDefaultSizeToTitles();
    }

    appendDefaultSizeToTitles() {
      if (
        !this.preselectedFilters ||
        !this.preselectedFilters.size ||
        !this.preselectedFilters.size.length
      ) {
        return;
      }
      const currentSize = this.selectedFilters.size;
      const defaultSize = this.preselectedFilters.size[0];
      if (!currentSize || currentSize[0] !== defaultSize) return;

      const sizeFilterBlock = this.querySelector(
        '.filter-block[data-category="size"]'
      );
      if (sizeFilterBlock && sizeFilterBlock.offsetParent !== null) return;

      this.querySelectorAll(".product-item__title").forEach((title) => {
        if (title.dataset.sizeAppended === "true") return;
        title.innerHTML += ` / ${defaultSize}`;
        title.dataset.sizeAppended = "true";
      });
    }
  
    viewSwitch(event) {
      this.container.classList.toggle("multi");
      localStorage.setItem(
        "gridView_v2",
        this.container.classList.contains("multi") ? "multi" : "single"
      );
      // Reset the mobile "show more" limit to the new view's page size (12/24).
      this._mobileVisible = null;
      if (this._applyPagination) this._applyPagination();
    }
  
    mobileFilterOpen() {
      const allFilters = this.querySelector("#all-filters");
      allFilters.classList.add("is-visible");
  
      const overlay = document.createElement("div");
      overlay.classList.add("overlay");
      allFilters.parentNode.insertBefore(overlay, allFilters);
  
      document.body.classList.add("overflow-hidden");
    }
  
    mobileFilterClose() {
      const allFilters = this.querySelector("#all-filters");
      allFilters.classList.remove("is-visible");
      document.body.classList.remove("overflow-hidden");
      this.querySelector(".all-products-body .overlay").remove();
    }
  }
  
  customElements.define("all-products", Products);

  /* Shop-all category tabs -> drive the "category" filter radios */
  $(document).on("click", ".shopall_tab", function () {
    var value = $(this).data("tab");

    $(".shopall_tab").removeClass("is-active").attr("aria-selected", "false");
    $(this).addClass("is-active").attr("aria-selected", "true");

    var input = $(
      'all-products .filter-block__wrapper input[name="category"][value="' + value + '"]'
    );
    if (input.length && !input.prop("checked")) {
      input[0].click();
    }
  });

  /* Keep the tabs in sync when the category filter is changed directly */
  $(document).on("click", 'all-products input[name="category"]', function () {
    var value = $(this).val();
    $(".shopall_tab").removeClass("is-active").attr("aria-selected", "false");
    $('.shopall_tab[data-tab="' + value + '"]')
      .addClass("is-active")
      .attr("aria-selected", "true");
  });
